import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Input, Select, Button, Tooltip } from 'antd';

import { useTranslation } from 'react-i18next';
import Table from '@components/Table';
import { dateFormat, getDateInISOFormat } from '@utils/formatters';
import { ClockCircleOutlined, FileSearchOutlined } from '@ant-design/icons';
import useSwr from '@hooks/useSwr';
import SelectCustomers from '@components/SelectCustomers';
import StatusMenu, { StatusItemProps } from '@components/StatusMenu';
import SessionContext from '@providers/Session/SessionContext';
import useSearchParams from '@hooks/useSearchParams';
import CustomTag, { TagColors } from '@components/CustomTag';
import ActionTypes from '@utils/deviceActions';
import QuickRangePicker from '@components/DatePicker/QuickRangePicker';
import moment from 'moment';
import AutoServicesDetails, { ServiceProps } from './AutoServicesDetails';

const { Search } = Input;

export type AutoServicesActions =
  | ActionTypes.ACTIVATION_MULTI_CONNECT
  | ActionTypes.CONTRACT_SUSPEND
  | ActionTypes.PROVISIONAL_WITHDRAW
  | ActionTypes.CONTRACT_MOVEMENT;

const AutoServices: React.FC = () => {
  const { t } = useTranslation(['auto-services', 'device-actions']);

  const { isRoot, hasCustomersRoles } = useContext(SessionContext);

  const [msisdnValue, setMsisdnValue] = useState('');
  const [iccidValue, setIccidValue] = useState('');
  const [protocolValue, setProtocolValue] = useState('');
  const [createdByValue, setCreatedByValue] = useState('');
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [serviceDetails, setServiceDetails] = useState<
    ServiceProps | undefined
  >();

  const {
    setSearchParams,
    hasValuesInExtraParams,
    clearSearchParams,
    searchParams,
  } = useSearchParams({
    type: 'pagination',
    defaultParams: ['operationType'],
    extraParams: [
      'statusService',
      'protocol',
      'createdBy',
      'customerId',
      'startDate',
      'endDate',
      'msisdn',
      'iccid',
    ],
  });

  const {
    msisdn,
    iccid,
    crmBatchProtocol,
    statusService,
    protocol,
    createdBy,
    customerId,
    operationType,
    startDate,
    endDate,
    page,
    linesPerPage,
  } = useMemo(() => searchParams, [searchParams]);

  useEffect(() => {
    setCreatedByValue(createdBy);
  }, [createdBy]);

  const params = useMemo(
    () => ({
      status: statusService,
      customerId,
      protocol,
      operationType,
      createdBy,
      startDate,
      endDate,
      msisdn,
      iccid,
      crmBatchProtocol,
      page: page ? Number(page) - 1 : 0,
      linesPerPage: linesPerPage ?? 20,
    }),
    [
      statusService,
      customerId,
      protocol,
      createdBy,
      operationType,
      startDate,
      endDate,
      page,
      linesPerPage,
      msisdn,
      iccid,
      crmBatchProtocol,
    ],
  );

  useEffect(() => {
    setMsisdnValue(msisdn);
  }, [msisdn]);

  useEffect(() => {
    setIccidValue(iccid);
  }, [iccid]);

  useEffect(() => {
    setProtocolValue(protocol);
  }, [protocol]);

  const { data: dataOperation } = useSwr('/service-proxy/operation', {
    params,
  });

  const formattedData = useMemo(
    () =>
      dataOperation?.content.map((action: any) => {
        if (action.vendor) {
          const { vendor, ...restAction } = action;
          return { ...restAction, operator: vendor };
        }
        if (action.operator) {
          return {
            ...action,
            operator: action.operator
              .replaceAll(',', '')
              .replace('[', '')
              .replace(']', ''),
          };
        }
        return action;
      }),
    [dataOperation],
  );

  const columnListStatus = useCallback(
    (success: number, row: any) => {
      const list: { label: string; color: TagColors; value?: number }[] = [
        {
          label: 'SUCCESS',
          color: 'green',
          value: success,
        },
        {
          label: 'PROCESSING',
          color: 'blue',
          value: row.processing,
        },
        {
          label: 'ERROR',
          color: 'red',
          value: row.error,
        },
      ];

      return (
        <>
          {list.map(({ label, color, value }) => (
            <Tooltip key={label} title={t(`sim-status.${label}`)}>
              <CustomTag
                icon={<ClockCircleOutlined />}
                color={color}
                style={{ cursor: 'pointer' }}
              >
                {value ?? 0}
              </CustomTag>
            </Tooltip>
          ))}
        </>
      );
    },
    [t],
  );

  const actionFromType = useMemo(
    // @ts-ignore
    () => operationType && operationType,
    [operationType],
  );

  const columns = useMemo(
    () => [
      {
        title: t('table.operationProtocol'),
        dataIndex: 'crmBatchProtocol',
      },
      ...(isRoot
        ? [
            {
              title: t('table.client'),
              dataIndex: 'customerName',
            },
          ]
        : []),
      {
        title: t('table.number-of-simcards'),
        dataIndex: 'success',
        align: 'center' as const,
        render: columnListStatus,
      },
      {
        title: t('table.actionType'),
        dataIndex: 'operationType',
        align: 'center' as const,
        render: (action: string) => t(`device-actions:labels.${action}`),
      },
      {
        title: t('table.createdAt'),
        dataIndex: 'createdAt',
        render: (value: string) => value && dateFormat(value),
      },
      {
        title: t('table.createdBy'),
        dataIndex: 'createdBy',
      },
      {
        title: t('table.status'),
        dataIndex: 'status',
        render: (value: string) => t(`status.${value}`),
      },
      ...([
        ActionTypes.CONTRACT_SUSPEND,
        ActionTypes.PROVISIONAL_WITHDRAW,
      ].includes(Number(operationType))
        ? [
            {
              title: t('table.scheduling'),
              dataIndex: 'processData',
              key: 'date',
              align: 'center' as const,
              render: (processData: string) => {
                if (processData && JSON.parse(processData)?.date) {
                  return JSON.parse(processData)?.date;
                }
                return (
                  processData &&
                  JSON.parse(processData)?.period &&
                  t('days', { count: Number(JSON.parse(processData)?.period) })
                );
              },
            },
          ]
        : []),
      {
        title: t('actions'),
        dataIndex: 'actions',
        width: 20,
        render: (_: any, record: any) => (
          <Tooltip title={t('see-details')}>
            <Button
              style={{ padding: '0 8px' }}
              onClick={() => {
                setIsDetailsOpen(true);
                const {
                  createdAt,
                  protocol: prt,
                  crmBatchProtocol: batchPrt,
                  status: sts,
                  createdBy: crBy,
                  processData: prcData,
                } = record;
                setServiceDetails({
                  action:
                    record?.operationType === '8'
                      ? '10'
                      : record?.operationType,
                  protocol: prt,
                  crmBatchProtocol: batchPrt,
                  createdAt,
                  createdBy: crBy,
                  status: sts,
                  processData: prcData,
                });
              }}
            >
              <FileSearchOutlined />
            </Button>
          </Tooltip>
        ),
      },
    ],
    [t, columnListStatus, isRoot, operationType],
  );

  const handleDateChange = useCallback(
    (values: any) => {
      const start = getDateInISOFormat(values.startDate);
      const end = getDateInISOFormat(values.endDate);
      setSearchParams({ startDate: start, endDate: end });
    },
    [setSearchParams],
  );

  const tableFilters = useMemo(
    () => [
      {
        visible: hasCustomersRoles && isRoot,
        label: t('table.client'),
        element: (
          <SelectCustomers
            value={customerId && Number(customerId)}
            onChange={value => setSearchParams({ customerId: value })}
          />
        ),
      },
      {
        visible: true,
        label: t('table.status'),
        element: (
          <Select
            optionFilterProp="label"
            options={[
              {
                value: 'PROCESSING',
                label: t('status.PROCESSING'),
              },
              {
                value: 'FINISHED',
                label: t('status.FINISHED'),
              },
            ]}
            style={{ width: '100%' }}
            value={statusService}
            onChange={value => setSearchParams({ statusService: value })}
            allowClear
          />
        ),
      },
      {
        visible: true,
        label: t('table.msisdn'),
        element: (
          <Search
            onSearch={value => setSearchParams({ msisdn: value })}
            onChange={({ target: { value } }) => setMsisdnValue(value)}
            value={msisdnValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        label: t('table.iccid'),
        element: (
          <Search
            onSearch={value => setSearchParams({ iccid: value })}
            onChange={({ target: { value } }) => setIccidValue(value)}
            value={iccidValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        label: t('table.protocol'),
        element: (
          <Search
            onSearch={value => setSearchParams({ protocol: value })}
            onChange={({ target: { value } }) => setProtocolValue(value)}
            value={protocolValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        label: t('table.createdBy'),
        element: (
          <Search
            onChange={({
              target: { value },
            }: ChangeEvent<HTMLInputElement>) => {
              setCreatedByValue(value);
            }}
            onSearch={(value: string) => {
              setSearchParams({
                createdBy: value,
              });
            }}
            allowClear
            value={createdByValue}
          />
        ),
      },
      {
        visible: true,
        label: t('table.createdAt'),
        element: (
          <QuickRangePicker
            onChange={handleDateChange}
            defaultValue={
              startDate
                ? [moment(startDate, 'YYYYMMDD'), moment(endDate, 'YYYYMMDD')]
                : undefined
            }
            value={
              startDate
                ? [moment(startDate, 'YYYYMMDD'), moment(endDate, 'YYYYMMDD')]
                : undefined
            }
            style={{ width: '100%' }}
          />
        ),
      },
    ],
    [
      hasCustomersRoles,
      isRoot,
      t,
      customerId,
      statusService,
      msisdnValue,
      iccidValue,
      protocolValue,
      createdByValue,
      handleDateChange,
      startDate,
      endDate,
      setSearchParams,
    ],
  );

  const statusMenuOperations = useMemo(
    (): StatusItemProps[] =>
      ['0', '9', '10', '34', '39'].map(actionKey => ({
        label: t(`device-actions:labels.${actionKey}`),
        value: actionKey,
      })),
    [t],
  );

  return (
    <div>
      <AutoServicesDetails
        service={serviceDetails}
        isOpen={isDetailsOpen}
        setIsOpen={setIsDetailsOpen}
      />
      <StatusMenu
        itemActive={operationType}
        data={statusMenuOperations}
        handleSelectedCard={item => {
          setSearchParams({
            operationType:
              item.value !== operationType ? item.value : undefined,
          });
        }}
        style={{ marginBottom: 16 }}
        showIconClear
      />

      <Table
        columns={columns}
        data={formattedData || []}
        loading={!dataOperation}
        title={
          actionFromType || actionFromType === 0
            ? t(`device-actions:labels.${actionFromType}`)
            : t('title')
        }
        tableKey="autoServices"
        onTableChange={setSearchParams}
        filters={{
          list: tableFilters,
          hasValuesInFilters: hasValuesInExtraParams,
          onClearFilters: () => clearSearchParams(),
        }}
        pagination={{ page, linesPerPage, total: dataOperation?.totalElements }}
      />
    </div>
  );
};

export default AutoServices;
